/**
 * Ayupath Teaser API Client
 * Connects the React app to the Azure Functions backend.
 */

import { API_BASE_URL } from './apiBase';

/**
 * Generate a UUID v4 for idempotency keys
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Submit a teaser generation request.
 * 
 * @param {Object} data - Form data
 * @param {string} data.name - User's name
 * @param {string} data.dob - Birth date (YYYY-MM-DD)
 * @param {string} data.birthTime - Birth time (HH:mm 24-hour)
 * @param {Object} data.location - Location data from autocomplete
 * @param {string} data.email - User's email
 * @returns {Promise<{requestId: string, statusUrl: string}>}
 */
export async function submitTeaserRequest(data) {
  const idempotencyKey = generateUUID();

  const payload = {
    birth_date: data.dob,
    birth_time: data.birthTime,
    birth_time_confidence: data.birthTimeConfidence || "unknown",
    name: data.name || '',
    location: {
      lat: data.location.lat,
      lon: data.location.lon,
      city: data.location.city || '',
      region: data.location.region || '',
      country: data.location.country || '',
    },
    email: data.email,
    focus_area: data.focusArea || "",
    landing_variant: data.landingVariant || "vedic-timeline-v1",
    service_email_consent: data.serviceEmailConsent === true,
    marketing_consent: data.marketingConsent === true,
    turnstile_token: data.turnstileToken || undefined,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

  let response;
  try {
    response = await fetch(`${API_BASE_URL}/teaser`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify(payload),
    });
  } catch (fetchErr) {
    clearTimeout(timeoutId);
    if (fetchErr.name === 'AbortError') {
      throw new Error('Request timed out. The server may be starting up — please try again in a moment.');
    }
    throw fetchErr;
  }
  clearTimeout(timeoutId);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || error.errors?.join('; ') || `Server error (${response.status})`);
  }

  const result = await response.json();
  
  // Store request metadata in localStorage for retrieval
  localStorage.setItem(`teaser-${result.requestId}`, JSON.stringify({
    requestId: result.requestId,
    name: data.name,
    email: data.email,
    submittedAt: new Date().toISOString(),
    idempotencyKey,
    landingVariant: data.landingVariant || "vedic-timeline-v1",
  }));

  return result;
}

/**
 * Replace the birth time on an existing request and trigger a rerun.
 *
 * @param {string} requestId
 * @param {string} birthTime - HH:mm, 24-hour
 */
export async function correctBirthTime(requestId, birthTime) {
  const response = await fetch(`${API_BASE_URL}/teaser/${requestId}/birth-time`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ birth_time: birthTime, birth_time_confidence: 'exact' }),
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error("This reading has already been updated the maximum number of times.");
    }
    if (response.status === 404) {
      throw new Error('We could not find that reading.');
    }
    throw new Error('We could not update your birth time. Please try again.');
  }

  return response.json();
}

/**
 * Record yes/no answers to the past-window questions.
 *
 * keepalive lets the request finish while the page moves on to the preview.
 *
 * @param {string} requestId
 * @param {Object<string, "yes"|"no">} answers
 * @param {boolean} researchConsent
 */
export async function submitLifeAnswers(requestId, answers, researchConsent, eventYears = {}) {
  const consent = researchConsent === true;
  const body = { answers, research_consent: consent };
  // The backend refuses years without consent, so never send them otherwise.
  if (consent && Object.keys(eventYears).length) body.event_years = eventYears;
  const response = await fetch(`${API_BASE_URL}/teaser/${requestId}/answers`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error('Could not save answers');
  }
}

/**
 * Send the reader's feedback on their preview: { kind: 'quick', rating, text }
 * from under the preview, or { kind: 'survey', ... } from the survey page.
 */
export async function submitFeedback(requestId, feedback) {
  const response = await fetch(`${API_BASE_URL}/teaser/${requestId}/feedback`, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(feedback),
  });
  if (!response.ok) {
    throw new Error('Could not save feedback');
  }
}

/**
 * Send a paying reader's question for their full report. The backend
 * refuses it (409) unless the order has been paid.
 */
export async function submitIntake(requestId, intake) {
  const response = await fetch(`${API_BASE_URL}/teaser/${requestId}/intake`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(intake),
  });
  if (!response.ok) {
    const error = new Error('Could not save your question');
    error.status = response.status;
    throw error;
  }
}

/**
 * Poll for teaser status and result.
 * 
 * @param {string} requestId - The request ID from submitTeaserRequest
 * @param {AbortSignal} [signal] - Optional signal to cancel the request
 * @returns {Promise<Object>} Status object with { status, teaser?, error? }
 */
export async function getTeaserStatus(requestId, signal) {
  const response = await fetch(`${API_BASE_URL}/teaser/${requestId}`, { signal });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Request not found');
    }
    throw new Error('Failed to fetch teaser status');
  }

  return response.json();
}

/**
 * Poll until the teaser is DONE or FAILED, or the wait budget is exhausted.
 *
 * Generation normally lands well inside the budget; the longer window exists so
 * the reading can be revealed on-page instead of deferring the user to email.
 *
 * @param {string} requestId
 * @param {Object} [options]
 * @param {Function} [options.onProgress] - Called after each poll with {status, elapsedMs}
 * @param {number} [options.maxWaitMs] - Total time to keep polling
 * @param {AbortSignal} [options.signal] - Cancels polling (e.g. on unmount)
 * @returns {Promise<Object>} Final result, or {status:'TIMEOUT'} if the budget runs out
 */
export async function pollTeaserUntilComplete(requestId, options = {}) {
  const {
    onProgress,
    maxWaitMs = 180000, // 3 minutes
    signal,
  } = options;

  const startedAt = Date.now();
  let attempt = 0;

  while (Date.now() - startedAt < maxWaitMs) {
    if (signal?.aborted) {
      return { status: 'CANCELLED' };
    }

    let result;
    try {
      result = await getTeaserStatus(requestId, signal);
    } catch (err) {
      if (err.name === 'AbortError' || signal?.aborted) {
        return { status: 'CANCELLED' };
      }
      // Transient network/cold-start errors shouldn't end the reveal attempt.
      result = { status: 'PENDING' };
    }

    if (onProgress) {
      onProgress({ ...result, elapsedMs: Date.now() - startedAt });
    }

    if (result.status === 'DONE' || result.status === 'FAILED') {
      return result;
    }

    // Poll quickly at first, then ease off to reduce load on the backend.
    attempt += 1;
    const delay = attempt <= 6 ? 2000 : attempt <= 15 ? 4000 : 8000;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  return { status: 'TIMEOUT' };
}
